import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CaretRight, Check, WifiSlash, MapPin
} from '@phosphor-icons/react';

const BRAND = {
  bg: '#F5F3EE',
  text: '#2D2A26',
  textSecondary: '#86868b',
  gold: '#B8860B',
  goldLight: 'rgba(184, 134, 11, 0.08)',
  dotInactive: '#D4D4D4',
};

const SLIDES = [
  {
    id: 'hero',
    headline: 'Your Business,\nSimplified.',
    subtext: 'Everything you need to run your business – in one powerful app.',
  },
  {
    id: 'sell',
    headline: 'Sell\nAnywhere.',
    subtext: 'Accept payments, track every sale, and manage inventory – even without internet.',
  },
  {
    id: 'know',
    headline: 'Know Your\nNumbers.',
    subtext: 'Real-time reports, expense tracking, and staff performance at your fingertips.',
  },
  {
    id: 'grow',
    headline: 'Grow With\nConfidence.',
    subtext: 'Multi-branch support, customer insights, and tools to scale your business.',
  },
];

// ─── Slide 1: Logo ───
const HeroIllustration = () => (
  <div className="flex flex-col items-center">
    <img
      src="/images/logo.png"
      alt="SmartPOS Africa"
      className="w-28 h-28 rounded-[30px] mb-5"
      style={{ boxShadow: '0 16px 40px rgba(184, 134, 11, 0.2)' }}
    />
    <p
      className="text-sm font-semibold tracking-[0.12em] uppercase"
      style={{ color: BRAND.textSecondary }}
    >
      Smart POS Africa
    </p>
  </div>
);

// ─── Slide 2: Stylized phone with receipt ───
const SellIllustration = () => (
  <div className="relative" style={{ width: 180, height: 220 }}>
    {/* Phone body */}
    <div
      className="absolute inset-0 rounded-2xl border-2 overflow-hidden"
      style={{ borderColor: '#E0DDD8', background: '#fff' }}
    >
      {/* Screen content – mini receipt */}
      <div className="px-5 pt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="w-16 h-2 rounded-full" style={{ background: '#E8E6E1' }} />
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: BRAND.goldLight }}
          >
            <Check weight="bold" size={12} color={BRAND.gold} />
          </div>
        </div>
        {/* Receipt lines */}
        {[0.7, 0.5, 0.85, 0.4].map((w, i) => (
          <div key={i} className="flex items-center justify-between mb-3">
            <div
              className="h-2 rounded-full"
              style={{ width: `${w * 60}%`, background: '#E8E6E1' }}
            />
            <div
              className="h-2 rounded-full"
              style={{ width: '20%', background: i === 0 ? `${BRAND.gold}20` : '#E8E6E1' }}
            />
          </div>
        ))}
        {/* Total line */}
        <div
          className="mt-4 pt-3 flex items-center justify-between"
          style={{ borderTop: '1.5px dashed #E0DDD8' }}
        >
          <span className="text-xs font-bold" style={{ color: BRAND.text }}>Total</span>
          <span className="text-sm font-extrabold" style={{ color: BRAND.gold }}>GHS 450</span>
        </div>
      </div>
    </div>
    {/* Floating offline badge */}
    <div
      className="absolute -right-4 top-6 flex items-center gap-1.5 px-3 py-2 rounded-xl"
      style={{
        background: '#fff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #F0EDE8',
      }}
    >
      <WifiSlash weight="bold" size={14} color={BRAND.textSecondary} />
      <span className="text-[11px] font-semibold" style={{ color: BRAND.text }}>Offline</span>
    </div>
  </div>
);

// ─── Slide 3: Mini dashboard card ───
const KnowIllustration = () => {
  const bars = [35, 55, 40, 70, 50, 65, 85];
  return (
    <div
      className="rounded-2xl border p-5 relative"
      style={{
        width: 220,
        background: '#fff',
        borderColor: '#F0EDE8',
        boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
      }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-medium mb-0.5" style={{ color: BRAND.textSecondary }}>Revenue</p>
          <p className="text-lg font-extrabold" style={{ color: BRAND.text }}>GHS 12,400</p>
        </div>
        <div
          className="px-2.5 py-1 rounded-full text-[11px] font-bold"
          style={{ background: BRAND.goldLight, color: BRAND.gold }}
        >
          +22%
        </div>
      </div>
      {/* Bar chart */}
      <div className="flex items-end gap-[5px]" style={{ height: 64 }}>
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-md"
            style={{
              height: `${h}%`,
              background: i === bars.length - 1 ? BRAND.gold : '#E8E6E1',
            }}
          />
        ))}
      </div>
      {/* Bottom stats */}
      <div className="flex justify-between mt-4 pt-3" style={{ borderTop: '1px solid #F0EDE8' }}>
        {[
          { label: 'Orders', value: '248' },
          { label: 'Items', value: '1,204' },
          { label: 'Profit', value: '68%' },
        ].map((s, i) => (
          <div key={i} className="text-center">
            <p className="text-sm font-bold" style={{ color: BRAND.text }}>{s.value}</p>
            <p className="text-[10px]" style={{ color: BRAND.textSecondary }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Slide 4: Branch hub ───
const GrowIllustration = () => {
  const branches = [
    { label: 'HQ', x: 90, y: 0 },
    { label: 'Accra', x: 175, y: 55 },
    { label: 'Kumasi', x: 155, y: 145 },
    { label: 'Tamale', x: 25, y: 145 },
    { label: 'Takoradi', x: 0, y: 55 },
  ];
  const cx = 90, cy = 90;

  return (
    <div className="relative" style={{ width: 200, height: 200 }}>
      {/* Connection lines */}
      <svg className="absolute inset-0" width="200" height="200">
        {branches.map((b, i) => (
          <line
            key={i}
            x1={cx + 10} y1={cy + 10}
            x2={b.x + 15} y2={b.y + 15}
            stroke="#E0DDD8"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
        ))}
      </svg>
      {/* Center node */}
      <img
        src="/images/logo.png"
        alt="S"
        className="absolute w-[52px] h-[52px] rounded-2xl z-10"
        style={{
          left: cx - 6, top: cy - 6,
          boxShadow: '0 8px 24px rgba(184, 134, 11, 0.2)',
        }}
      />
      {/* Branch nodes */}
      {branches.map((b, i) => (
        <div
          key={i}
          className="absolute flex flex-col items-center"
          style={{ left: b.x, top: b.y }}
        >
          <div
            className="w-[30px] h-[30px] rounded-lg flex items-center justify-center border"
            style={{
              background: '#fff',
              borderColor: '#E8E6E1',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <MapPin weight="bold" size={13} color={BRAND.textSecondary} />
          </div>
          <span
            className="text-[9px] font-semibold mt-1"
            style={{ color: BRAND.textSecondary }}
          >
            {b.label}
          </span>
        </div>
      ))}
    </div>
  );
};

const ILLUSTRATIONS = [HeroIllustration, SellIllustration, KnowIllustration, GrowIllustration];

const AppOnboarding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const autoAdvanceRef = useRef(null);
  const navigate = useNavigate();

  const totalSlides = SLIDES.length;

  const goToSlide = useCallback((index) => {
    if (transitioning || index === currentSlide) return;
    setTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setTransitioning(false), 500);
  }, [transitioning, currentSlide]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }
  }, [currentSlide, totalSlides, goToSlide]);

  useEffect(() => {
    autoAdvanceRef.current = setInterval(() => {
      setCurrentSlide(prev => prev < totalSlides - 1 ? prev + 1 : prev);
    }, 5000);
    return () => clearInterval(autoAdvanceRef.current);
  }, [totalSlides]);

  const resetAutoAdvance = () => {
    clearInterval(autoAdvanceRef.current);
    autoAdvanceRef.current = setInterval(() => {
      setCurrentSlide(prev => prev < totalSlides - 1 ? prev + 1 : prev);
    }, 5000);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) >= 50) {
      resetAutoAdvance();
      if (distance > 0 && currentSlide < totalSlides - 1) goToSlide(currentSlide + 1);
      else if (distance < 0 && currentSlide > 0) goToSlide(currentSlide - 1);
    }
  };

  const handleGetStarted = () => {
    localStorage.setItem('smartpos_onboarding_seen', 'true');
    navigate('/login');
  };

  const handleSkip = () => {
    localStorage.setItem('smartpos_onboarding_seen', 'true');
    navigate('/login');
  };

  const slide = SLIDES[currentSlide];
  const isLastSlide = currentSlide === totalSlides - 1;
  const Illustration = ILLUSTRATIONS[currentSlide];

  return (
    <div
      className="fixed inset-0 overflow-hidden font-['Manrope'] select-none flex flex-col safe-area-top safe-area-bottom"
      style={{ background: BRAND.bg }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ─── Skip ─── */}
      {!isLastSlide && (
        <button
          onClick={handleSkip}
          className="absolute top-14 right-6 z-50 text-sm font-medium bg-transparent border-none cursor-pointer transition-colors duration-200"
          style={{ color: BRAND.textSecondary }}
        >
          Skip
        </button>
      )}

      {/* ─── Illustration area ─── */}
      <div className="flex-[3] flex items-center justify-center">
        <div
          key={`illust-${currentSlide}`}
          style={{ animation: 'onb-fade-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          <Illustration />
        </div>
      </div>

      {/* ─── Text + Controls ─── */}
      <div className="flex-[2] px-8 pb-10 flex flex-col justify-end">
        <h1
          key={`h-${currentSlide}`}
          className="text-[38px] leading-[1.08] font-extrabold tracking-tight mb-3"
          style={{
            color: BRAND.text,
            animation: 'onb-fade-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {slide.headline.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h1>

        <p
          key={`p-${currentSlide}`}
          className="text-[15px] leading-relaxed mb-10 max-w-[300px]"
          style={{
            color: BRAND.textSecondary,
            animation: 'onb-fade-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.08s both',
          }}
        >
          {slide.subtext}
        </p>

        {/* Dots + CTA */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => { resetAutoAdvance(); goToSlide(i); }}
                className="border-none bg-transparent p-0 cursor-pointer"
                aria-label={`Go to slide ${i + 1}`}
              >
                <div
                  className="h-[6px] rounded-full transition-all duration-400 ease-out"
                  style={{
                    width: i === currentSlide ? '24px' : '6px',
                    background: i === currentSlide ? BRAND.gold : BRAND.dotInactive,
                  }}
                />
              </button>
            ))}
          </div>

          {isLastSlide ? (
            <button
              onClick={handleGetStarted}
              className="flex items-center gap-2 px-7 py-4 rounded-full font-bold text-[15px] text-white border-none cursor-pointer transition-all duration-200 active:scale-95"
              style={{
                background: BRAND.gold,
                boxShadow: '0 8px 24px rgba(184, 134, 11, 0.25)',
              }}
            >
              Get Started
              <ArrowRight weight="bold" size={16} />
            </button>
          ) : (
            <button
              onClick={() => { resetAutoAdvance(); nextSlide(); }}
              className="w-14 h-14 rounded-full flex items-center justify-center border-none cursor-pointer transition-all duration-200 active:scale-90"
              style={{
                background: BRAND.gold,
                boxShadow: '0 8px 24px rgba(184, 134, 11, 0.2)',
              }}
            >
              <CaretRight weight="bold" size={20} color="#fff" />
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes onb-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AppOnboarding;
